import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { LegalFooter } from "@/components/LegalFooter";
import { LEGAL_PAGES } from "@/pages/legalContent";
import { api } from "@/lib/api";
import { resolveAgbPriceRows } from "@/lib/legalCatalog";

// Go-Live (Juni 2026) – Rechtliche Pflichtseiten für CLartext (Impressum/
// Datenschutz/AGB/AVV). Redaktionell finalisiert auf Basis bestätigter Stammdaten;
// vor Veröffentlichung anwaltlich zu prüfen. Login-frei erreichbar.

const Block = ({ part, agbPriceRows }) => {
  if (typeof part === "string") {
    return <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{part}</p>;
  }
  if (part.ul) {
    return (
      <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base text-slate-600">
        {part.ul.map((li, i) => <li key={i}>{li}</li>)}
      </ul>
    );
  }
  if (part.kv || part.priceTable) {
    // M4.7 LAUNCH GATE: Die AGB §6-Preistabelle wird – sofern eine ausdrücklich
    // gebundene, veröffentlichte Katalog-Version vorliegt – aus dieser abgeleitet
    // (agbPriceRows). Andernfalls bleiben die statischen In-Kraft-Zeilen (part.kv).
    const dynamic = part.priceTable && Array.isArray(agbPriceRows);
    const rows = dynamic ? agbPriceRows : part.kv;
    return (
      <dl
        className="grid sm:grid-cols-[220px_1fr] gap-x-6 gap-y-2"
        data-testid={part.priceTable ? "agb-price-table" : undefined}
        data-price-source={part.priceTable ? (dynamic ? "catalog" : "static") : undefined}
      >
        {rows.map(([k, v], i) => (
          <div key={i} className="contents">
            <dt className="text-sm font-medium text-slate-500">{k}</dt>
            <dd className="text-sm sm:text-base text-slate-800">{v}</dd>
          </div>
        ))}
      </dl>
    );
  }
  return null;
};

export default function Legal({ pageTitle, pageKey }) {
  const [agbPriceRows, setAgbPriceRows] = useState(null);

  useEffect(() => {
    document.title = `${pageTitle} – CLartext`;
    return () => {
      document.title = "CLartext";
    };
  }, [pageTitle]);

  // M4.7 LAUNCH GATE: AGB §6 löst ausschließlich gegen die ausdrücklich gebundene,
  // veröffentlichte Katalog-Version auf (lib/legalCatalog.js). Nie gegen CURRENT,
  // nie gegen unveröffentlichte Versionen. Bei null/Fehler bleiben statische Werte.
  useEffect(() => {
    if (pageKey !== "agb") return undefined;
    let active = true;
    api
      .getPublicCatalog()
      .then((cat) => {
        if (active) setAgbPriceRows(resolveAgbPriceRows(cat));
      })
      .catch(() => {
        if (active) setAgbPriceRows(null);
      });
    return () => {
      active = false;
    };
  }, [pageKey]);

  const data = LEGAL_PAGES[pageKey];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="legal-page" data-legal-key={pageKey}>
      <div className="flex-1 w-full max-w-3xl mx-auto px-4 py-12 sm:py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-medium">
          <ShieldCheck className="w-3.5 h-3.5" />
          CLartext
        </div>

        <h1
          className="font-heading text-4xl sm:text-5xl font-black text-slate-900 leading-tight tracking-tight mt-8"
          data-testid="legal-page-title"
        >
          {pageTitle}
        </h1>

        {data?.intro && <p className="text-base text-slate-500 mt-3">{data.intro}</p>}

        <div className="mt-8 space-y-8" data-testid="legal-content">
          {(data?.sections || []).map((sec, i) => (
            <section key={i} className="space-y-3">
              <h2 className="text-lg font-semibold text-slate-900">{sec.h}</h2>
              {sec.body.map((part, j) => <Block key={j} part={part} agbPriceRows={agbPriceRows} />)}
            </section>
          ))}
        </div>

        <Link
          to="/"
          data-testid="legal-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mt-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Startseite
        </Link>
      </div>

      <div className="border-t border-slate-100 py-6">
        <LegalFooter />
      </div>
    </div>
  );
}
